import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Send,
  AlertTriangle,
  Clock,
  CheckCircle,
  FileText,
  Upload,
  Search,
  Users,
  Plus,
  Edit2,
  Trash2
} from 'lucide-react';
import NavbarComptable from '../components/NavbarComptable';
import SidebarComptable from '../components/SidebarComptable';
import useAuth from '../hooks/useAuth';

const API_BASE = 'https://api-ec.cinef-expertises.com/api';

const EcheancesComptable = () => {
  const { getToken, isAuthenticated, userType, isLoading, deconnexion } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEcheance, setSelectedEcheance] = useState(null);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const emptyForm = {
    titre: '',
    description: '',
    date_echeance: '',
    montant: '',
    statut: 'a_venir',
    client_id: '',
    fichier: null
  };

  const [formData, setFormData] = useState(emptyForm);
  const [echeances, setEcheances] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Vérification d'authentification (seulement après le chargement initial)
  useEffect(() => {
    if (!isLoading && (!isAuthenticated || userType !== 'expert')) {
      console.log('❌ EcheancesComptable: Accès non autorisé, déconnexion...');
      deconnexion();
    }
  }, [isAuthenticated, userType, isLoading, deconnexion]);

  // --- Initial load
  useEffect(() => {
    if (isAuthenticated && userType === 'expert') {
      fetchClients();
      fetchEcheances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userType]);

  // --- Fetch clients
  const fetchClients = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('Token manquant');
        deconnexion();
        return;
      }

      const res = await axios.get(`${API_BASE}/clients`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });
      // Si ton API renvoie { data: [...] } ou directement un array, on gère les deux cas
      const data = res.data?.data ?? res.data;
      setClients(Array.isArray(data) ? data : []);
      const first = Array.isArray(data) && data.length ? data[0] : null;
      setSelectedClient(first);
      setFormData(prev => ({ ...prev, client_id: first?.id ?? '' }));
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
      if (err.response?.status === 401) {
        deconnexion();
      }
      setClients([]);
    }
  };

  // --- Fetch échéances envoyées par l'expert (comptable)
  const fetchEcheances = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('Token manquant');
        deconnexion();
        return;
      }

      const res = await axios.get(`${API_BASE}/expert/echeances`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = res.data?.data ?? res.data;
      setEcheances(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur API :", err.response?.data || err);
      if (err.response?.status === 401) {
        deconnexion();
      }
    }
  };



  // --- Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, fichier: e.target.files[0] || null }));
  };

  // --- Open create modal
  const openCreateModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData(prev => ({ ...emptyForm, client_id: selectedClient?.id ?? '' }));
    setShowFormModal(true);
  };

  // --- Open edit modal
  const openEditModal = (echeance) => {
    setIsEditing(true);
    setEditingId(echeance.id);
    setFormData({
      titre: echeance.titre ?? '',
      description: echeance.description ?? '',
      date_echeance: echeance.date_echeance ? echeance.date_echeance.split('T')[0] : '',
      montant: echeance.montant ?? '',
      statut: echeance.statut ?? 'a_venir',
      client_id: echeance.client_id ?? echeance.client?.id ?? '',
      fichier: null // file will be uploaded only if user chooses a new one
    });
    // Set selected client in top bar
    const client = clients.find(c => c.id === (echeance.client_id ?? echeance.client?.id));
    if (client) setSelectedClient(client);
    setShowFormModal(true);
  };

  // --- Submit create or update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 🔥 RÉCUPÉRATION DU TOKEN EXPERT
      const token = getToken();

      if (!token) {
        alert("Token expert manquant. Veuillez vous reconnecter.");
        deconnexion();
        return;
      }
      setSubmitting(true);
      const formPayload = new FormData();
      formPayload.append('titre', formData.titre);
      formPayload.append('description', formData.description);
      formPayload.append('date_echeance', formData.date_echeance);
      formPayload.append('montant', formData.montant);
      formPayload.append('statut', formData.statut);
      formPayload.append('client_id', formData.client_id);

      if (formData.fichier) {
        formPayload.append('fichier', formData.fichier);
      }

      // ==============================================
      //  MODE ÉDITION
      // ==============================================
      if (isEditing && editingId) {
        const res = await axios.post(
          `${API_BASE}/expert/echeances/${editingId}?_method=PUT`,
          formPayload,
          {
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );


        const newE = res.data?.data ?? res.data;

        setEcheances(prev =>
          prev.map(item =>
            item.id === editingId
              ? {
                ...newE,
                client_name:
                  newE.client_name ||
                  newE.client?.nom ||
                  newE.client?.name ||
                  item.client_name
              }
              : item
          )
        );

        alert('Échéance mise à jour avec succès !');

      } else {
        // ==============================================
        // ➕ MODE CRÉATION
        // ==============================================
        const res = await axios.post(
          `${API_BASE}/expert/echeances`,
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "Authorization": `Bearer ${token}` // 🔥 AJOUT ICI
            }
          }
        );

        const created = res.data?.data ?? res.data;

        const normalized = {
          ...created,
          client_name:
            created.client_name ||
            created.client?.nom ||
            created.client?.name ||
            clients.find(c => c.id === created.client_id)?.nom ||
            null
        };

        setEcheances(prev => [normalized, ...prev]);
        setSubmitting(false);
        alert('Échéance envoyée avec succès !');
      }

      // RESET FORM
      setFormData({
        titre: '',
        description: '',
        date_echeance: '',
        montant: '',
        statut: 'a_venir',
        client_id: selectedClient?.id || '',
        fichier: null
      });

      setShowFormModal(false);
      setIsEditing(false);
      setEditingId(null);

    } catch (err) {
      console.error("Erreur lors de l'envoi de l'échéance:", err);
      const msg =
        err?.response?.data?.message ??
        'Erreur lors de l’envoi de l’échéance.';
      alert(msg);
    } finally {
    setSubmitting(false);
  }
};


// --- Delete
const handleDeleteEcheance = async (id) => {
  if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette échéance ?')) return;
  try {
    const token = getToken();
    if (!token) {
      deconnexion();
      return;
    }

    await axios.delete(`${API_BASE}/expert/echeances/${id}`, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Accept": "application/json"
      }
    });

    setEcheances(prev => prev.filter(e => e.id !== id));
    alert('Échéance supprimée.');
  } catch (err) {
    console.error('Erreur lors de la suppression:', err);
    if (err.response?.status === 401) {
      deconnexion();
    } else {
      alert('Impossible de supprimer cette échéance.');
    }
  }
};

// --- Voir details
const handleVoirDetails = (echeance) => {
  setSelectedEcheance(echeance);
  setShowDetailsModal(true);
};

// --- Helpers
const getStatutStyle = (statut) => {
  switch (statut) {
    case 'urgent': return 'urgent';
    case 'a_venir': return 'a_venir';
    case 'planifie': return 'planifie';
    default: return '';
  }
};

const getStatutIcon = (statut) => {
  switch (statut) {
    case 'urgent': return <AlertTriangle size={16} className="status-icon urgent" />;
    case 'a_venir': return <Clock size={16} className="status-icon a_venir" />;
    case 'planifie': return <CheckCircle size={16} className="status-icon planifie" />;
    default: return null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  // handle possible datetime
  const d = dateString.split('T')[0];
  return new Date(d).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const calculateDaysRemaining = (dateEcheance) => {
  if (!dateEcheance) return '-';
  const today = new Date();
  const echeance = new Date(dateEcheance.split('T')[0]);
  const diffTime = echeance - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// --- Filtering & searching
const filteredEcheances = (echeances || []).filter(item => {
  // filter by statut
  if (filterStatut !== 'tous' && item.statut !== filterStatut) return false;

  // search by titre or client name
  const lowerSearch = (searchTerm || '').toLowerCase();
  if (!lowerSearch) return true;

  const titreMatch = item.titre?.toLowerCase().includes(lowerSearch);
  const clientName = (item.client_name || item.client?.nom || item.client?.name || '').toLowerCase();
  const clientMatch = clientName.includes(lowerSearch);

  return titreMatch || clientMatch;
});

// --- UI return
return (
  <>
    <div className="app-container">
      <SidebarComptable
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="main-wrapper">
        <NavbarComptable
          selectedClient={selectedClient}
          onClientChange={setSelectedClient}
          clients={clients}
        />

        <main className="main-content">
          {/* Header */}
          <div className="page-header">
            <div className="header-content">
              <div className="header-text">
                <h1 className="page-title">Gestion des Échéances Fiscales</h1>
                <p className="page-subtitle">
                  Envoyez et gérez les échéances fiscales de vos clients
                </p>
              </div>
              <button
                className="btn-primary"
                onClick={openCreateModal}
              >
                <Plus size={20} />
                Nouvelle échéance
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="stats-grid">
            <div className="stat-card urgent">
              <h3>Urgentes</h3>
              <p>{echeances.filter(e => e.statut === 'urgent').length}</p>
            </div>
            <div className="stat-card a_venir">
              <h3>À venir</h3>
              <p>{echeances.filter(e => e.statut === 'a_venir').length}</p>
            </div>
            <div className="stat-card planifie">
              <h3>Planifiées</h3>
              <p>{echeances.filter(e => e.statut === 'planifie').length}</p>
            </div>
            <div className="stat-card total">
              <h3>Total envoyées</h3>
              <p>{echeances.length}</p>
            </div>
          </div>

          {/* Filtres et recherche */}
          <div className="filters-section">
            <div className="search-box">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher par titre ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterStatut === 'tous' ? 'active' : ''}`}
                onClick={() => setFilterStatut('tous')}
              >
                Tous
              </button>
              <button
                className={`filter-btn ${filterStatut === 'urgent' ? 'active urgent' : ''}`}
                onClick={() => setFilterStatut('urgent')}
              >
                Urgentes
              </button>
              <button
                className={`filter-btn ${filterStatut === 'a_venir' ? 'active a_venir' : ''}`}
                onClick={() => setFilterStatut('a_venir')}
              >
                À venir
              </button>
              <button
                className={`filter-btn ${filterStatut === 'planifie' ? 'active planifie' : ''}`}
                onClick={() => setFilterStatut('planifie')}
              >
                Planifiées
              </button>
            </div>
          </div>

          {/* Liste des échéances */}
          <div className="echeances-container">
            <div className="container-header">
              <h2>Échéances envoyées ({filteredEcheances.length})</h2>
            </div>

            {filteredEcheances.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <p>Aucune échéance trouvée</p>
              </div>
            ) : (
              <div className="echeances-list">
                {filteredEcheances.map((echeance) => (
                  <div key={echeance.id} className="echeance-item">
                    <div className="echeance-content">
                      <div className="echeance-main">
                        {getStatutIcon(echeance.statut)}
                        <div className="echeance-info">
                          <h4>{echeance.titre}</h4>
                          <p className="echeance-client">
                            <Users size={14} />
                            {echeance.client_name || echeance.client?.nom || echeance.client?.name || '—'}
                          </p>
                          <p>{echeance.description}</p>
                          <span>Échéance : {formatDate(echeance.date_echeance)}</span>
                          <span>({calculateDaysRemaining(echeance.date_echeance)} jours restants)</span>
                        </div>
                      </div>

                      <div className="echeance-actions">
                        <p className="echeance-amount">
                          {parseFloat(echeance.montant || 0).toLocaleString('fr-FR')} XOF
                        </p>
                        <span className={`status-badge ${getStatutStyle(echeance.statut)}`}>
                          {echeance.statut?.toUpperCase()}
                        </span>
                        <div className="action-buttons">
                          <button
                            className="btn-action primary"
                            onClick={() => handleVoirDetails(echeance)}
                          >
                            <FileText size={14} />
                            Détails
                          </button>
                          <button
                            className="btn-action warning"
                            onClick={() => openEditModal(echeance)}
                          >
                            <Edit2 size={14} />
                            Modifier
                          </button>
                          <button
                            className="btn-action danger"
                            onClick={() => handleDeleteEcheance(echeance.id)}
                          >
                            <Trash2 size={14} />
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>

    {/* Modal Formulaire (Create / Edit) */}
    {showFormModal && (
      <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{isEditing ? 'Modifier une échéance' : 'Nouvelle échéance fiscale'}</h2>
            <button className="modal-close" onClick={() => setShowFormModal(false)}>×</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    name="client_id"
                    value={formData.client_id}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setFormData(prev => ({ ...prev, client_id: selectedId }));
                      const client = clients.find(c => c.id === parseInt(selectedId));
                      setSelectedClient(client);
                    }}
                    required
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nom || client.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Statut *</label>
                  <select
                    name="statut"
                    value={formData.statut}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="urgent">Urgent</option>
                    <option value="a_venir">À venir</option>
                    <option value="planifie">Planifié</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Titre de l'échéance *</label>
                  <input
                    type="text"
                    name="titre"
                    value={formData.titre}
                    onChange={handleInputChange}
                    placeholder="Ex: TVA Mensuelle"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Détails de l'échéance..."
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date d'échéance *</label>
                  <input
                    type="date"
                    name="date_echeance"
                    value={formData.date_echeance}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Montant (XOF) *</label>
                  <input
                    type="number"
                    name="montant"
                    value={formData.montant}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Fichier joint (PDF)</label>
                  <div className="file-upload">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xlsx,.jpg,.png,.jpeg"
                      onChange={handleFileChange}
                      id="fichier"
                    />
                    <label htmlFor="fichier" className="file-upload-label">
                      <Upload size={20} />
                      {formData.fichier ? formData.fichier.name : 'Choisir un fichier (PDF, DOC, etc.)'}
                    </label>
                  </div>
                </div>
              </div>
            </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-action secondary"
                  onClick={() => { setShowFormModal(false); setIsEditing(false); setEditingId(null); }}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-action primary" disabled={submitting}>
                  <Send size={14} />
                  {isEditing ? 'Mettre à jour' : 'Envoyer l\'échéance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {showDetailsModal && selectedEcheance && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de l'échéance</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Client:</span>
                <span className="detail-value">{selectedEcheance.client_name || selectedEcheance.client?.nom || selectedEcheance.client?.name || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Titre:</span>
                <span className="detail-value">{selectedEcheance.titre}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Description:</span>
                <span className="detail-value">{selectedEcheance.description}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date d'échéance:</span>
                <span className="detail-value">{formatDate(selectedEcheance.date_echeance)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Montant:</span>
                <span className="detail-value">
                  {parseFloat(selectedEcheance.montant || 0).toLocaleString('fr-FR')} XOF
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Statut:</span>
                <span className={`status-badge ${getStatutStyle(selectedEcheance.statut)}`}>
                  {selectedEcheance.statut?.toUpperCase()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date d'envoi:</span>
                <span className="detail-value">{formatDate(selectedEcheance.date_envoi)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Jours restants:</span>
                <span className="detail-value">
                  {calculateDaysRemaining(selectedEcheance.date_echeance)} jours
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-action secondary" onClick={() => setShowDetailsModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

    {/* Styles modernisés et harmonisés */}
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 320px;
        }

        .main-content {
          flex: 1;
          padding: 32px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 138, 68, 0.3) transparent;
        }

        .main-content::-webkit-scrollbar {
          width: 6px;
        }

        .main-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .main-content::-webkit-scrollbar-thumb {
          background: rgba(0, 138, 68, 0.2);
          border-radius: 10px;
        }

        /* Header Section */
        .page-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .header-text {
          flex: 1;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 6px 0;
          background: linear-gradient(135deg, #dc3545, #008A44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 16px;
          font-weight: 500;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #dc3545, #008A44);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          letter-spacing: -0.2px;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(220, 53, 69, 0.3);
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: #ffffff;
          padding: 28px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: currentColor;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
        }

        .stat-card.urgent {
          color: #dc3545;
        }

        .stat-card.a_venir {
          color: #f59e0b;
        }

        .stat-card.planifie {
          color: #10b981;
        }

        .stat-card.total {
          color: #3b82f6;
        }

        .stat-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: currentColor;
          margin: 0 0 12px 0;
          opacity: 0.9;
        }

        .stat-card p {
          font-size: 36px;
          font-weight: 700;
          color: currentColor;
          margin: 0;
          line-height: 1;
        }

        /* Filters Section */
        .filters-section {
          background: white;
          padding: 20px;
          border-radius: 16px;
          margin-bottom: 24px;
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .search-box {
          position: relative;
          flex: 1;
          min-width: 300px;
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 48px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .search-input:focus {
          outline: none;
          border-color: #008A44;
          box-shadow: 0 0 0 3px rgba(0, 138, 68, 0.1);
        }

        .filter-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 10px 20px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #64748b;
          font-family: inherit;
        }

        .filter-btn:hover {
          border-color: #008A44;
          color: #008A44;
        }

        .filter-btn.active {
          color: white;
        }

        .filter-btn.active:not(.urgent):not(.a_venir):not(.planifie) {
          background: linear-gradient(135deg, #dc3545, #008A44);
          border-color: #dc3545;
        }

        .filter-btn.active.urgent {
          background: #dc3545;
          border-color: #dc3545;
        }

        .filter-btn.active.a_venir {
          background: #f59e0b;
          border-color: #f59e0b;
        }

        .filter-btn.active.planifie {
          background: #10b981;
          border-color: #10b981;
        }

        /* Echeances Container */
        .echeances-container {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .container-header {
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(0, 138, 68, 0.05) 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .container-header h2 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          letter-spacing: -0.3px;
        }

        .empty-state {
          padding: 60px 20px;
          text-align: center;
          color: #64748b;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .empty-state svg {
          color: #94a3b8;
        }

        .echeances-list {
          padding: 0;
        }

        .echeance-item {
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }

        .echeance-item:last-child {
          border-bottom: none;
        }

        .echeance-item:hover {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .echeance-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .echeance-main {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          flex: 1;
          min-width: 0;
        }

        .status-icon {
          margin-top: 2px;
          flex-shrink: 0;
        }

        .status-icon.urgent {
          color: #dc3545;
        }

        .status-icon.a_venir {
          color: #f59e0b;
        }

        .status-icon.planifie {
          color: #10b981;
        }

        .echeance-info {
          flex: 1;
          min-width: 0;
        }

        .echeance-info h4 {
          font-size: 17px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 6px 0;
          line-height: 1.3;
        }

        .echeance-client {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #008A44;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .echeance-info p {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 12px 0;
          line-height: 1.4;
        }

        .echeance-info span {
          font-size: 14px;
          color: #374151;
          font-weight: 500;
        }

        .echeance-actions {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
          flex-shrink: 0;
        }

        .echeance-amount {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.urgent {
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: #ffffff;
        }

        .status-badge.a_venir {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff;
        }

        .status-badge.planifie {
          background: linear-gradient(135deg, #10b981, #059669);
          color: #ffffff;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .btn-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .btn-action:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        .btn-action.primary {
          background: linear-gradient(135deg, #10b981, #059669);
          color: #ffffff;
        }

        .btn-action.primary:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .btn-action.warning {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff;
        }

        .btn-action.warning:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
        }

        .btn-action.danger {
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: #ffffff;
        }

        .btn-action.danger:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }

        .btn-action.secondary {
          background: #ffffff;
          color: #64748b;
          border: 2px solid #e5e7eb;
        }

        .btn-action.secondary:hover:not(:disabled) {
          border-color: #008A44;
          color: #008A44;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .modal-content {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: modalSlideIn 0.3s ease-out;
        }

        .modal-content.large {
          max-width: 800px;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(0, 138, 68, 0.05) 100%);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          background: linear-gradient(135deg, #dc3545, #008A44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 32px;
          color: #64748b;
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .modal-body {
          padding: 28px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 15px;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #008A44;
          box-shadow: 0 0 0 3px rgba(0, 138, 68, 0.1);
        }

        .file-upload {
          position: relative;
        }

        .file-upload input[type="file"] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .file-upload-label {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05), rgba(0, 138, 68, 0.05));
          border: 2px dashed #dc3545;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 15px;
          color: #374151;
        }

        .file-upload-label:hover {
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(0, 138, 68, 0.1));
          border-color: #008A44;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 600;
          color: #64748b;
          font-size: 14px;
        }

        .detail-value {
          font-weight: 600;
          color: #1e293b;
          font-size: 15px;
          text-align: right;
        }

        .modal-footer {
          padding: 20px 28px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          flex-wrap: wrap;
          background: #f8fafc;
        }

        /* Spinner animation */
        .spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1023px) {
          .main-wrapper {
            margin-left: 0;
          }

          .main-content {
            padding: 20px;
          }

          .page-title {
            font-size: 28px;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .page-title {
            font-size: 24px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .btn-primary {
            width: 100%;
            justify-content: center;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            min-width: 100%;
          }

          .filter-buttons {
            justify-content: stretch;
          }

          .filter-btn {
            flex: 1;
          }

          .echeance-content {
            flex-direction: column;
            align-items: stretch;
          }

          .echeance-actions {
            align-items: stretch;
          }

          .action-buttons {
            flex-direction: column;
          }

          .btn-action {
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .echeances-container {
            border-radius: 12px;
          }

          .container-header,
          .echeance-item {
            padding: 20px;
          }
        }
      `}</style>
  </>
);
};

export default EcheancesComptable;